import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Clock, Info, Printer, RefreshCw, Ruler, Scale, SkipForward } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { BulkResult } from '../ui/BulkResult';
import { CopyableText } from '../ui/CopyableText';
import { ErrorState } from '../ui/ErrorState';
import { ProgressBar } from '../ui/ProgressBar';
import { dispatchDeliveriesToCourier } from '../../features/deliveries/deliveriesThunks';
import { formatCourier } from '../../features/deliveries/deliveriesUtils';
import { selectProgress } from '../../features/feedback/feedbackSlice';

const WHAT_HAPPENS = [
  {
    icon: Clock,
    text: 'Se procesan en segundo plano: el tracking nuevo aparece en el historial a medida que el courier responde.',
  },
  {
    icon: Ruler,
    text: 'Las medidas que les falten a los bultos se completan con 10 × 10 × 10 cm.',
  },
  {
    icon: Scale,
    text: 'El peso que falte se reparte del peso declarado del envío; si no hay o no alcanza, va 1 kg por bulto.',
  },
  {
    icon: RefreshCw,
    text: 'Si un envío ya tenía tracking, se crea uno nuevo y el anterior se anula solo en los couriers que funcionan sobre SimpliRoute. En los demás te listamos el anterior para que lo anules a mano.',
  },
  {
    icon: Printer,
    text: 'Las etiquetas cambian: reimprímelas cuando veas el tracking nuevo en el historial.',
  },
  {
    icon: SkipForward,
    text: 'Se omiten los envíos que ya van en camino o están finalizados.',
  },
];

const ROW_LIST = 'max-h-40 overflow-y-auto divide-y divide-border border border-border rounded-xl';

/** The order behind a row, or the shipment itself when the backend found none. */
const shipmentOf = (row) => (row.order_id ? `#${row.order_id}` : `Envío ${row.delivery_id}`);

const Section = ({ title, children }) => (
  <div className="flex flex-col gap-2">
    <h3 className="text-xs font-bold uppercase tracking-wider text-text-muted">{title}</h3>
    {children}
  </div>
);

const DispatchResult = ({ result, onClose }) => {
  const toCancel = result.queued.filter((row) => row.previous_cancellation === 'manual');
  const autoCancelled = result.queued.filter((row) => row.previous_cancellation === 'auto').length;
  const failureReasons = [...new Set(result.failed.map((row) => row.message))];

  return (
    <BulkResult
      result={{
        requested: result.requested,
        succeeded: result.queued,
        skipped: result.skipped,
        failed: result.failed,
      }}
      succeededLabel="En cola"
      onClose={onClose}
    >
      {/* Its own scroll: with long lists the dialog outgrows a laptop screen,
          and the shared Modal does not scroll. */}
      <div className="flex flex-col gap-4 max-h-[45vh] overflow-y-auto pr-1">
        {failureReasons.length > 0 && (
          <ErrorState
            variant="inline"
            title={`${result.failed.length} sin enviar`}
            description={`${failureReasons.join(' ')} Quedan seleccionados para que puedas reintentar.`}
          />
        )}

        {result.skipped.length > 0 && (
          <Section title="Omitidos">
            <ul className={ROW_LIST}>
              {result.skipped.map((row) => (
                <li
                  key={row.delivery_id}
                  data-testid="dispatch-courier-skipped"
                  data-reason={row.reason}
                  className="flex flex-col px-3 py-2"
                >
                  <span className="text-sm font-semibold text-ninja-dark dark:text-white">
                    {shipmentOf(row)}
                    {row.reference_id && (
                      <span className="font-normal text-text-secondary"> · {row.reference_id}</span>
                    )}
                  </span>
                  <span className="text-xs text-text-secondary">{row.message}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {toCancel.length > 0 && (
          <Section title="Anular a mano en el courier">
            <p className="text-xs text-text-secondary">
              Su courier no deja anular el tracking anterior desde aquí. Cuando cambie su tracking
              en el historial (puede tardar unos minutos), anula el anterior en el sistema del
              courier. Si no cambia, no lo anules: el anterior sigue siendo el válido.
            </p>
            <ul className={ROW_LIST}>
              {toCancel.map((row) => (
                <li
                  key={row.delivery_id}
                  data-testid="dispatch-courier-manual-cancel"
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-semibold text-ninja-dark dark:text-white">
                      {shipmentOf(row)}
                    </span>
                    <span className="text-xs text-text-secondary">{formatCourier(row.courier)}</span>
                  </div>
                  <CopyableText
                    value={row.previous_tracking}
                    maxWidth="140px"
                    className="text-xs font-medium text-ninja-dark dark:text-white"
                  />
                </li>
              ))}
            </ul>
          </Section>
        )}

        {autoCancelled > 0 && (
          <div className="flex items-start gap-2.5 bg-ninja-gray dark:bg-[#111] border border-border rounded-xl px-4 py-3">
            <Info size={16} className="text-text-muted flex-shrink-0 mt-0.5" />
            <p className="text-xs text-text-secondary">
              En {autoCancelled} {autoCancelled === 1 ? 'envío' : 'envíos'} la visita anterior se
              elimina sola, porque su courier funciona sobre SimpliRoute. Si ya estaba en una ruta,
              ese envío no se reenvía y queda como estaba.
            </p>
          </div>
        )}
      </div>
    </BulkResult>
  );
};

/**
 * Sends the selected shipments to their courier without the preparation rules.
 *
 * Shared by the three workspaces. It ends on a tally instead of closing,
 * because two outcomes need the operator: the shipments that were skipped, and
 * the old trackings their courier cannot cancel on its own.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {number[]} props.deliveryIds
 * @param {(failedIds: number[]) => void} [props.onFinished] - Called when the
 *   result is closed, with the delivery ids that did not go out, so the page
 *   can keep them selected for a retry.
 */
export const DispatchToCourierModal = ({ isOpen, onClose, deliveryIds = [], onFinished }) => {
  const dispatch = useDispatch();

  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // A selection over 100 goes out in batches; the thunk reports which one.
  const [requestId, setRequestId] = useState(null);
  const progress = useSelector(selectProgress(requestId));

  const handleClose = () => {
    if (sending) return;
    const finished = result;
    setError(null);
    setResult(null);
    onClose();
    if (finished) onFinished?.(finished.failed.map((row) => row.delivery_id));
  };

  const handleConfirm = async () => {
    setSending(true);
    setError(null);

    const running = dispatch(dispatchDeliveriesToCourier(deliveryIds));
    setRequestId(running.requestId);

    try {
      setResult(await running.unwrap());
    } catch (err) {
      setError(typeof err === 'string' ? err : 'No se pudieron enviar los envíos al courier');
    } finally {
      setSending(false);
    }
  };

  const count = deliveryIds.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Enviar al courier"
      testId="dispatch-courier-modal"
      busy={sending}
    >
      {result ? (
        <DispatchResult result={result} onClose={handleClose} />
      ) : (
        <div className="flex flex-col gap-5">
          <p className="text-sm text-text-secondary text-center">
            Vas a mandar {count} {count === 1 ? 'envío' : 'envíos'} a su courier sin pasar por la
            preparación.
          </p>

          <ul className="flex flex-col gap-3">
            {WHAT_HAPPENS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-2.5 text-sm text-text-secondary">
                <Icon size={16} className="text-text-muted flex-shrink-0 mt-0.5" />
                <span>{text}</span>
              </li>
            ))}
          </ul>

          {error && <ErrorState variant="inline" title="No se pudo enviar" description={error} />}

          {sending && progress && (
            <ProgressBar
              value={progress.done}
              max={progress.total}
              label={progress.label}
              testId="bulk-progress"
            />
          )}

          <div className="flex items-center justify-end gap-3 mt-2">
            <Button variant="subtle" pill onClick={handleClose} disabled={sending}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              pill
              onClick={handleConfirm}
              loading={sending}
              loadingLabel="Enviando..."
              disabled={count === 0}
              data-testid="dispatch-courier-confirm"
            >
              Confirmar envío
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
